import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Pencil,
  Trash2,
  Plus,
  Search,
  Eye,
  Calendar,
  Package,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { authService } from "@/services/auth.service";
import { endpoints } from "@/config/endpoints";
import apiService from "@/services/api.service";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useNavigate } from "react-router-dom";
import {
  MATERIAL_CATEGORIES,
  MATERIAL_TYPES,
  MATERIAL_LIST,
  MATERIAL_ITEMS,
} from "@/utils/materialCategories";

interface ApprovalStage {
  role: string;
  status: string;
  timestamp?: string;
}

interface ApprovalHistory {
  role: string;
  status: string;
  timestamp: string;
  user?: {
    name: string;
    _id: string;
  };
}

interface Request {
  _id: string;
  requestId: string;
  itemName: string;
  quantity: string;
  status: string;
  deliveryStatus: string;
  phase: string;
  location: string;
  createdAt: string;
  createdBy: {
    name: string;
    email: string;
  };
  product: {
    title: string;
    type: string;
  };
  approvalFlow: {
    stages: ApprovalStage[];
  };
  approvalHistory: ApprovalHistory[];
  currentApprover: string;
  type: string;
  orderId: string;
  siteId: string;
}

type PhaseStatus = "planned" | "in-progress" | "completed" | "delayed";

interface Material {
  name: string;
  quantity: number;
  unit: string;
  category: string;
}

interface Phase {
  name: string;
  materials: Material[];
}

interface Tool {
  name: string;
  category: string;
  quantity: number;
  unit: string;
}

interface Site {
  _id: string;
  title: string;
  location: string;
  siteType: string;
  siteManager: {
    id: string;
    name: string;
    phone: string;
  };
  phases?: Phase[];
  materials?: Material[];
  tools?: Tool[];
}

interface User {
  _id: string;
  name: string;
  email: string;
  phone: string;
  roles: string[];
}

const siteTypes = [
  "Construction",
  "Mining",
  "Industrial",
  "Commercial",
  "Residential",
  "Other",
];

// Add this constant at the top of the file
const TOOL_CATEGORIES = [
  MATERIAL_CATEGORIES.HEAVY_MACHINERY_EQUIPMENT,
  MATERIAL_CATEGORIES.CONCRETE_EQUIPMENT,
  MATERIAL_CATEGORIES.CONCRETE_PUMP_PARTS,
  MATERIAL_CATEGORIES.SCAFFOLDING_SUPPORT_SYSTEMS,
  MATERIAL_CATEGORIES.SUPPORT_BEAMS,
  MATERIAL_CATEGORIES.FORMWORK_CASTING,
  MATERIAL_CATEGORIES.HAND_TOOLS,
  MATERIAL_CATEGORIES.MEASURING_LAYOUT_TOOLS,
  MATERIAL_CATEGORIES.TRANSPORT_MATERIAL_HANDLING,
  MATERIAL_CATEGORIES.STORAGE_CONTAINERS,
  MATERIAL_CATEGORIES.FASTENERS_HARDWARE,
  MATERIAL_CATEGORIES.SAFETY_EQUIPMENT,
  MATERIAL_CATEGORIES.FURNITURE_SITE_SETUP,
  MATERIAL_CATEGORIES.MISCELLANEOUS,
];

const Sites = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [newSiteType, setNewSiteType] = useState("");
  const [customTypeMode, setCustomTypeMode] = useState(false);
  const [availableSiteTypes, setAvailableSiteTypes] =
    useState<string[]>(siteTypes);

  // New states for phases and materials
  const [showPhasesSection, setShowPhasesSection] = useState(false);
  const [showMaterialsSection, setShowMaterialsSection] = useState(false);
  const [currentPhases, setCurrentPhases] = useState<Phase[]>([]);
  const [currentMaterials, setCurrentMaterials] = useState<Material[]>([]);
  const [newPhase, setNewPhase] = useState<Phase>({
    name: "",
    materials: [],
  });
  const [newMaterial, setNewMaterial] = useState<Material>({
    name: "",
    quantity: 1,
    unit: "pcs",
    category: "",
  });
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMaterialType, setSelectedMaterialType] = useState<
    "phase" | "tool"
  >("phase");
  const [editingPhaseIndex, setEditingPhaseIndex] = useState<number | null>(
    null
  );
  const [editingMaterialIndex, setEditingMaterialIndex] = useState<
    number | null
  >(null);

  // New states for tools
  const [showToolsSection, setShowToolsSection] = useState(false);
  const [newTool, setNewTool] = useState<Tool>({
    name: "",
    category: "",
    quantity: 1,
    unit: "pcs",
  });
  const [editingToolIndex, setEditingToolIndex] = useState<number | null>(null);

  const itemsPerPage = 5;
  const [formData, setFormData] = useState({
    title: "",
    location: "",
    siteType: "",
    siteManager: {
      id: "",
      name: "",
      phone: "",
    },
    phases: [] as Phase[],
    materials: [] as Material[],
    tools: [] as Tool[],
  });

  const { token } = authService.getStoredAuth();

  // Fetch sites
  const {
    data: sites,
    isLoading: sitesLoading,
    error: sitesError,
  } = useQuery<Site[]>({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await fetch(`${endpoints.sites.list}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          try {
            // Try to refresh the token
            const newToken = await authService.refreshToken();
            // Retry the request with new token
            const retryResponse = await fetch(`${endpoints.sites.list}`, {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            if (!retryResponse.ok) {
              throw new Error("Failed to fetch sites after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            // If refresh fails, logout
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to fetch sites");
      }
      return response.json();
    },
  });

  // Fetch site managers
  const {
    data: siteManagers,
    isLoading: managersLoading,
    error: managersError,
  } = useQuery<User[]>({
    queryKey: ["siteManagers"],
    queryFn: async () => {
      const response = await fetch(`${endpoints.auth.users}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(`${endpoints.auth.users}`, {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            if (!retryResponse.ok) {
              throw new Error(
                "Failed to fetch site managers after token refresh"
              );
            }
            const users = await retryResponse.json();
            // Filter and log the site managers
            const managers = users.filter((user: User) =>
              user.roles.includes("site_manager")
            );
            console.log("Fetched site managers:", managers);
            return managers;
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to fetch site managers");
      }
      const users = await response.json();
      // Filter and log the site managers
      const managers = users.filter((user: User) =>
        user.roles.includes("site_manager")
      );
      console.log("Fetched site managers:", managers);
      return managers;
    },
  });

  // Add new query for requests
  const { data: requests, isLoading: requestsLoading } = useQuery<Request[]>({
    queryKey: ["requests"],
    queryFn: async () => {
      const response = await fetch(`${endpoints.requests.list}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          try {
            const newToken = await authService.refreshToken();
            const retryResponse = await fetch(`${endpoints.requests.list}`, {
              headers: {
                Authorization: `Bearer ${newToken}`,
              },
            });
            if (!retryResponse.ok) {
              throw new Error("Failed to fetch requests after token refresh");
            }
            return retryResponse.json();
          } catch (error) {
            authService.logout();
            throw new Error("Session expired. Please login again.");
          }
        }
        throw new Error("Failed to fetch requests");
      }
      return response.json();
    },
  });

  const handleViewOpen = (site: Site) => {
    navigate(`/sites/${site._id}`);
  };

  const handleSiteManagerChange = (userId: string) => {
    console.log("Selected user ID:", userId);
    console.log("Available site managers:", siteManagers);

    const selectedManager = siteManagers?.find(
      (manager: User) => manager._id === userId
    );
    console.log("Selected manager:", selectedManager);

    if (selectedManager) {
      // Ensure all required fields are present
      if (
        !selectedManager._id ||
        !selectedManager.name ||
        !selectedManager.phone
      ) {
        console.error(
          "Missing required fields for site manager:",
          selectedManager
        );
        toast.error("Selected site manager is missing required information");
        return;
      }

      // Map the data exactly as it comes from the API
      const siteManagerData = {
        id: selectedManager._id,
        name: selectedManager.name,
        phone: selectedManager.phone,
      };
      console.log("Setting site manager data:", siteManagerData);

      setFormData((prev) => ({
        ...prev,
        siteManager: siteManagerData,
      }));
    } else {
      toast.error("Selected site manager not found");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Submitting form with data:", formData);

    // Validate site manager data
    if (
      !formData.siteManager?.id ||
      !formData.siteManager?.name ||
      !formData.siteManager?.phone
    ) {
      console.error("Missing site manager data:", formData.siteManager);
      toast.error("Please select a site manager with complete information");
      return;
    }

    // Create the site data with the complete site manager object
    const siteData = {
      title: formData.title,
      location: formData.location,
      siteType: formData.siteType,
      siteManager: {
        id: formData.siteManager.id,
        name: formData.siteManager.name,
        phone: formData.siteManager.phone,
      },
      phases: formData.phases.map((phase) => ({
        name: phase.name,
        materials: phase.materials || [],
      })),
      materials: formData.materials || [],
      tools:
        formData.tools?.map((tool) => ({
          name: tool.name,
          category: tool.category,
          quantity: tool.quantity,
          unit: tool.unit,
        })) || [],
    };
    console.log("Sending site data to server:", siteData);

    if (editingSite) {
      updateSiteMutation.mutate({
        _id: editingSite._id,
        ...siteData,
      });
    } else {
      createSiteMutation.mutate(siteData);
    }
  };

  const createSiteMutation = useMutation({
    mutationFn: async (newSite: Omit<Site, "_id">) => {
      console.log("Mutation received site data:", newSite);

      // Ensure site manager data is properly structured
      const siteData = {
        title: newSite.title,
        location: newSite.location,
        siteType: newSite.siteType,
        siteManager: {
          id: newSite.siteManager.id,
          name: newSite.siteManager.name,
          phone: newSite.siteManager.phone,
        },
        phases: newSite.phases || [],
        materials: newSite.materials || [],
        tools: newSite.tools || [], // Ensure tools are included
      };
      console.log("Sending to server:", siteData);

      const response = await fetch(`${endpoints.sites.create}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(siteData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Server error:", errorData);
        throw new Error(errorData.message || "Failed to create site");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      toast.success("Site created successfully");
      handleClose();
    },
    onError: (error: Error) => {
      console.error("Mutation error:", error);
      toast.error(error.message || "Failed to create site");
    },
  });

  const updateSiteMutation = useMutation({
    mutationFn: async (updatedSite: Site) => {
      const response = await fetch(`${endpoints.sites.site(updatedSite._id)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...updatedSite,
          tools:
            updatedSite.tools?.map((tool) => ({
              name: tool.name,
              category: tool.category,
              quantity: tool.quantity,
              unit: tool.unit,
            })) || [],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Check if this is a MongoDB duplicate key error
        if (
          errorData.message &&
          errorData.message.includes("E11000 duplicate key error")
        ) {
          throw new Error(
            "A user with this email already exists in the system."
          );
        }
        throw new Error(errorData.message || "Failed to update site");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      toast.success("Site updated successfully");
      handleClose();
    },
    onError: (error: Error) => {
      // Display the more specific error message
      toast.error(error.message || "Failed to update site");
    },
  });

  const deleteSiteMutation = useMutation({
    mutationFn: async (siteId: string) => {
      const response = await fetch(`${endpoints.sites.site(siteId)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to delete site");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sites"] });
      toast.success("Site deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete site");
    },
  });

  const handleOpen = () => {
    setFormData({
      title: "",
      location: "",
      siteType: "",
      siteManager: {
        id: "",
        name: "",
        phone: "",
      },
      phases: [],
      materials: [],
      tools: [],
    });
    setOpen(true);
  };

  const handleUpdateOpen = (site: Site) => {
    setEditingSite(site);
    setFormData({
      title: site.title,
      location: site.location,
      siteType: site.siteType,
      siteManager: {
        id: site.siteManager.id,
        name: site.siteManager.name,
        phone: site.siteManager.phone,
      },
      phases: site.phases || [],
      materials: site.materials || [],
      tools: site.tools || [],
    });
    setUpdateOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setUpdateOpen(false);
    setEditingSite(null);
    setFormData({
      title: "",
      location: "",
      siteType: "",
      siteManager: {
        id: "",
        name: "",
        phone: "",
      },
      phases: [],
      materials: [],
      tools: [],
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handlers for phases
  const handleAddPhase = () => {
    if (!newPhase.name) {
      toast.error("Phase name is required");
      return;
    }

    const isDuplicate = formData.phases.some(
      (phase) => phase.name.toLowerCase() === newPhase.name.toLowerCase()
    );

    if (isDuplicate) {
      toast.error("Phase with this name already exists");
      return;
    }

    if (editingPhaseIndex !== null) {
      // Update existing phase
      const updatedPhases = [...formData.phases];
      updatedPhases[editingPhaseIndex] = {
        name: newPhase.name,
        materials: [...(newPhase.materials || [])],
      };
      setFormData((prev) => ({ ...prev, phases: updatedPhases }));
      toast.success("Phase updated successfully");
    } else {
      // Add new phase with its materials
      setFormData((prev) => ({
        ...prev,
        phases: [
          ...prev.phases,
          {
            name: newPhase.name,
            materials: [...(newPhase.materials || [])],
          },
        ],
      }));
      toast.success("Phase added successfully");
    }

    // Reset form
    setNewPhase({
      name: "",
      materials: [],
    });
    setEditingPhaseIndex(null);
  };

  const handleEditPhase = (index: number) => {
    const phase = formData.phases[index];
    setNewPhase({
      name: phase.name,
      materials: [...(phase.materials || [])],
    });
    setEditingPhaseIndex(index);
  };

  const handleDeletePhase = (index: number) => {
    const updatedPhases = [...formData.phases];
    updatedPhases.splice(index, 1);
    setFormData((prev) => ({ ...prev, phases: updatedPhases }));
    toast.success("Phase removed");
  };

  // Handlers for materials
  const handleAddMaterial = () => {
    if (
      !newMaterial.name ||
      !newMaterial.quantity ||
      !newMaterial.unit ||
      !newMaterial.category
    ) {
      toast.error("Please fill in all material details");
      return;
    }

    if (selectedMaterialType === "phase") {
      // Add material to the current phase being edited
      setNewPhase((prev) => ({
        ...prev,
        materials: [...(prev.materials || []), { ...newMaterial }],
      }));
      toast.success("Material added to phase successfully");
    } else {
      // Add material to the general site materials
      setFormData((prev) => ({
        ...prev,
        materials: [...prev.materials, { ...newMaterial }],
      }));
      toast.success("Material added successfully");
    }

    // Reset the material form
    setNewMaterial({
      name: "",
      quantity: 1,
      unit: "pcs",
      category: "",
    });
    setSelectedCategory("");
  };

  const handleEditMaterial = (index: number, phaseIndex?: number) => {
    if (phaseIndex !== undefined) {
      const material = formData.phases[phaseIndex].materials[index];
      setNewMaterial({ ...material });
      setSelectedCategory(material.category);
      setEditingMaterialIndex(index);
      setEditingPhaseIndex(phaseIndex);
    } else {
      const material = formData.materials[index];
      setNewMaterial({ ...material });
      setSelectedCategory(material.category);
      setEditingMaterialIndex(index);
    }
  };

  const handleDeleteMaterial = (index: number, phaseIndex?: number) => {
    if (phaseIndex !== undefined) {
      setNewPhase((prev) => {
        const updatedMaterials = [...(prev.materials || [])];
        updatedMaterials.splice(index, 1);
        return { ...prev, materials: updatedMaterials };
      });
      toast.success("Material removed from phase");
    } else {
      setFormData((prev) => {
        const updatedMaterials = [...prev.materials];
        updatedMaterials.splice(index, 1);
        return { ...prev, materials: updatedMaterials };
      });
      toast.success("Material deleted successfully");
    }
  };

  // Handlers for tools
  const handleAddTool = () => {
    if (!newTool.name || !newTool.category) {
      toast.error("Please fill in all tool details");
      return;
    }

    if (editingToolIndex !== null) {
      // Update existing tool
      const updatedTools = [...(formData.tools || [])];
      updatedTools[editingToolIndex] = { ...newTool };
      setFormData((prev) => ({ ...prev, tools: updatedTools }));
      toast.success("Tool updated successfully");
    } else {
      // Add new tool
      setFormData((prev) => ({
        ...prev,
        tools: [...(prev.tools || []), { ...newTool }],
      }));
      toast.success("Tool added successfully");
    }

    // Reset form
    setNewTool({
      name: "",
      category: "",
      quantity: 1,
      unit: "pcs",
    });
    setEditingToolIndex(null);
  };

  const handleEditTool = (index: number) => {
    const tool = formData.tools?.[index];
    if (tool) {
      setNewTool({ ...tool });
      setEditingToolIndex(index);
    }
  };

  const handleDeleteTool = (index: number) => {
    setFormData((prev) => {
      const updatedTools = [...(prev.tools || [])];
      updatedTools.splice(index, 1);
      return { ...prev, tools: updatedTools };
    });
    toast.success("Tool removed");
  };

  // Filter and paginate sites
  const filteredSites = useMemo(() => {
    if (!sites) return [];
    return sites.filter(
      (site) =>
        site.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        site.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
        site.siteType.toLowerCase().includes(searchQuery.toLowerCase()) ||
        site.siteManager.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [sites, searchQuery]);

  const totalPages = Math.ceil(filteredSites.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSites = filteredSites.slice(startIndex, endIndex);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSites(currentSites.map((site) => site._id));
    } else {
      setSelectedSites([]);
    }
  };

  const handleSelectSite = (siteId: string, checked: boolean) => {
    if (checked) {
      setSelectedSites((prev) => [...prev, siteId]);
    } else {
      setSelectedSites((prev) => prev.filter((id) => id !== siteId));
    }
  };

  const handleBulkDelete = () => {
    if (selectedSites.length === 0) {
      toast.error("Please select at least one site to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedSites.length} selected site(s)?`
      )
    ) {
      Promise.all(
        selectedSites.map((siteId) => deleteSiteMutation.mutateAsync(siteId))
      )
        .then(() => {
          toast.success(`${selectedSites.length} site(s) deleted successfully`);
          setSelectedSites([]);
        })
        .catch((error) => {
          toast.error("Failed to delete some sites");
        });
    }
  };

  const handleAddCustomSiteType = () => {
    if (!newSiteType.trim()) {
      toast.error("Please enter a site type name");
      return;
    }

    if (availableSiteTypes.includes(newSiteType.trim())) {
      toast.error("This site type already exists");
      return;
    }

    setAvailableSiteTypes((prev) => [...prev, newSiteType.trim()]);
    setFormData((prev) => ({ ...prev, siteType: newSiteType.trim() }));
    setNewSiteType("");
    setCustomTypeMode(false);
    toast.success("New site type added");
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this site?")) {
      deleteSiteMutation.mutate(id);
    }
  };

  // Add this before the return statement
  const getRequestCount = (siteId: string) => {
    if (!requests) return 0;
    return requests.filter((request) => request.siteId === siteId).length;
  };

  if (sitesLoading || managersLoading) {
    return (
      <DashboardLayout title="Sites">
        <Card>
          <CardHeader>
            <CardTitle className="flex justify-between items-center">
              <span>Sites</span>
              <Button disabled className="bg-orange-500 hover:bg-orange-600">
                <Plus className="mr-2 h-4 w-4" />
                Add a Site
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Site Type</TableHead>
                  <TableHead>Site Manager</TableHead>
                  <TableHead>Requests</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[150px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[200px]" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-[100px]" />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Skeleton className="h-8 w-8" />
                        <Skeleton className="h-8 w-8" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Sites">
      <Card className="shadow-lg">
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl font-bold">Sites</CardTitle>
              <div className="flex gap-2">
                {selectedSites.length > 0 && (
                  <Button
                    variant="destructive"
                    onClick={handleBulkDelete}
                    className="bg-red-500 hover:bg-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Selected ({selectedSites.length})
                  </Button>
                )}
                <Button
                  onClick={() => handleOpen()}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Site
                </Button>
              </div>
            </div>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search sites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <input
                      type="checkbox"
                      checked={selectedSites.length === currentSites.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </TableHead>
                  <TableHead className="font-semibold">Title</TableHead>
                  <TableHead className="font-semibold">Location</TableHead>
                  <TableHead className="font-semibold">Site Type</TableHead>
                  <TableHead className="font-semibold">Site Manager</TableHead>
                  <TableHead className="font-semibold">Requests</TableHead>
                  <TableHead className="font-semibold text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentSites.map((site: Site) => (
                  <TableRow key={site._id} className="hover:bg-muted/50">
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedSites.includes(site._id)}
                        onChange={(e) =>
                          handleSelectSite(site._id, e.target.checked)
                        }
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </TableCell>
                    <TableCell className="font-medium">{site.title}</TableCell>
                    <TableCell>{site.location}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-medium text-orange-800">
                        {site.siteType}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {site.siteManager?.name || "N/A"}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {site.siteManager?.phone || "N/A"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {getRequestCount(site._id)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate(`/requests?siteId=${site._id}`)
                          }
                          className="text-blue-600 hover:text-blue-700"
                        >
                          View
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewOpen(site)}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          title="View site details"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setEditingSite(site);
                            setFormData({
                              title: site.title || "",
                              location: site.location || "",
                              siteType: site.siteType || "",
                              siteManager: {
                                id: site.siteManager?.id || "",
                                name: site.siteManager?.name || "",
                                phone: site.siteManager?.phone || "",
                              },
                              phases: site.phases || [],
                              materials: site.materials || [],
                              tools: site.tools || [],
                            });
                            setOpen(true);
                          }}
                          className="hover:bg-orange-100"
                          title="Edit site"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(site._id)}
                          className="hover:bg-red-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, filteredSites.length)} of{" "}
              {filteredSites.length} sites
            </div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className={
                      currentPage === 1 ? "pointer-events-none opacity-50" : ""
                    }
                  />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        isActive={currentPage === page}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className={
                      currentPage === totalPages
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-h-[90vh] overflow-y-auto w-[90vw] max-w-[1200px]">
              <DialogHeader className="flex flex-row justify-between">
                <DialogTitle>Add New Site</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter site title"
                      required
                      disabled={
                        updateSiteMutation.isPending ||
                        createSiteMutation.isPending
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Enter site location"
                      required
                      disabled={
                        updateSiteMutation.isPending ||
                        createSiteMutation.isPending
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="siteType">Site Type</Label>
                    {!customTypeMode ? (
                      <div className="flex gap-2">
                        <Select
                          value={formData.siteType}
                          onValueChange={(value) => {
                            if (value === "add_new") {
                              setCustomTypeMode(true);
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                siteType: value,
                              }));
                            }
                          }}
                          disabled={
                            updateSiteMutation.isPending ||
                            createSiteMutation.isPending
                          }
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select site type" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableSiteTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                            <SelectItem
                              value="add_new"
                              className="text-blue-600"
                            >
                              + Add New Type
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter new site type"
                          value={newSiteType}
                          onChange={(e) => setNewSiteType(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          onClick={handleAddCustomSiteType}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Add
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setCustomTypeMode(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="siteManager">Site Manager</Label>
                    <Select
                      value={formData.siteManager.id}
                      onValueChange={handleSiteManagerChange}
                      disabled={
                        updateSiteMutation.isPending ||
                        createSiteMutation.isPending
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select site manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {siteManagers?.map((manager: User) => (
                          <SelectItem key={manager._id} value={manager._id}>
                            {manager.name} ({manager.phone})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Phases Section */}
                  <div className="grid gap-2 mt-6 border-t pt-4">
                    <div className="flex justify-between items-center">
                      <Label>Construction Phases</Label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowPhasesSection(!showPhasesSection)}
                        className="text-xs"
                      >
                        {showPhasesSection ? "Hide" : "Add Phases"}{" "}
                        <Calendar className="ml-2 h-4 w-4" />
                      </Button>
                    </div>

                    {showPhasesSection && (
                      <div className="border rounded-md p-4 mt-2">
                        <div className="grid gap-4">
                          {/* Phase Form */}
                          <div className="grid grid-cols-1 gap-3">
                            <div className="grid gap-2">
                              <Label htmlFor="phaseName">Phase Name</Label>
                              <Input
                                id="phaseName"
                                value={newPhase.name}
                                onChange={(e) =>
                                  setNewPhase((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }))
                                }
                                placeholder="Enter phase name (e.g., Phase One)"
                              />
                            </div>

                            {/* Materials Section for Phase */}
                            <div className="grid gap-4 mt-4">
                              <div className="flex justify-between items-center">
                                <Label>Materials Needed for this Phase</Label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() =>
                                    setShowMaterialsSection(
                                      !showMaterialsSection
                                    )
                                  }
                                  className="text-xs"
                                >
                                  {showMaterialsSection
                                    ? "Hide"
                                    : "Add Materials"}{" "}
                                  <Package className="ml-2 h-4 w-4" />
                                </Button>
                              </div>

                              {showMaterialsSection && (
                                <div className="border rounded-md p-4">
                                  <div className="grid gap-4">
                                    {/* Material Form */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div className="grid gap-2">
                                        <Label htmlFor="materialCategory">
                                          Material Category
                                        </Label>
                                        <Select
                                          value={newMaterial.category}
                                          onValueChange={(value) => {
                                            setNewMaterial((prev) => ({
                                              ...prev,
                                              category: value,
                                              name: "", // Reset name when category changes
                                            }));
                                          }}
                                        >
                                          <SelectTrigger>
                                            <SelectValue placeholder="Select material category" />
                                          </SelectTrigger>
                                          <SelectContent
                                            position="popper"
                                            className="w-full"
                                          >
                                            {Object.entries(
                                              MATERIAL_CATEGORIES
                                            ).map(([key, value]) => (
                                              <SelectItem
                                                key={key}
                                                value={value}
                                              >
                                                {value}
                                              </SelectItem>
                                            ))}
                                            <SelectItem
                                              value="custom"
                                              className="text-blue-600"
                                            >
                                              + Add Custom Category
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>

                                      {newMaterial.category &&
                                        newMaterial.category !== "custom" && (
                                          <div className="grid gap-2">
                                            <Label htmlFor="materialType">
                                              Material Type
                                            </Label>
                                            <Select
                                              value={newMaterial.name}
                                              onValueChange={(value) => {
                                                if (value === "custom") return;
                                                setNewMaterial((prev) => ({
                                                  ...prev,
                                                  name: value,
                                                }));
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Select material type" />
                                              </SelectTrigger>
                                              <SelectContent
                                                position="popper"
                                                className="w-full"
                                              >
                                                {MATERIAL_TYPES[
                                                  newMaterial.category
                                                ]?.map((type) => (
                                                  <SelectItem
                                                    key={type}
                                                    value={type}
                                                  >
                                                    {type}
                                                  </SelectItem>
                                                ))}
                                                <SelectItem
                                                  value="custom"
                                                  className="text-blue-600"
                                                >
                                                  + Add Custom Type
                                                </SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}

                                      {newMaterial.category === "custom" && (
                                        <div className="grid gap-2">
                                          <Label htmlFor="customMaterialCategory">
                                            Custom Category Name
                                          </Label>
                                          <Input
                                            id="customMaterialCategory"
                                            value={newMaterial.category}
                                            onChange={(e) =>
                                              setNewMaterial((prev) => ({
                                                ...prev,
                                                category: e.target.value,
                                              }))
                                            }
                                            placeholder="Enter category name"
                                          />
                                        </div>
                                      )}

                                      {newMaterial.name === "custom" && (
                                        <div className="grid gap-2">
                                          <Label htmlFor="customMaterialName">
                                            Custom Material Name
                                          </Label>
                                          <Input
                                            id="customMaterialName"
                                            value=""
                                            onChange={(e) =>
                                              setNewMaterial((prev) => ({
                                                ...prev,
                                                name: e.target.value,
                                              }))
                                            }
                                            placeholder="Enter material name"
                                          />
                                        </div>
                                      )}

                                      <div className="grid gap-2">
                                        <Label htmlFor="materialQuantity">
                                          Quantity
                                        </Label>
                                        <Input
                                          id="materialQuantity"
                                          type="number"
                                          min="1"
                                          value={newMaterial.quantity}
                                          onChange={(e) =>
                                            setNewMaterial((prev) => ({
                                              ...prev,
                                              quantity: Number(e.target.value),
                                            }))
                                          }
                                        />
                                      </div>

                                      <div className="grid gap-2">
                                        <Label htmlFor="materialUnit">
                                          Unit
                                        </Label>
                                        <Select
                                          value={newMaterial.unit}
                                          onValueChange={(value) =>
                                            setNewMaterial((prev) => ({
                                              ...prev,
                                              unit: value,
                                            }))
                                          }
                                        >
                                          <SelectTrigger>
                                            <SelectValue placeholder="Select unit" />
                                          </SelectTrigger>
                                          <SelectContent
                                            position="popper"
                                            className="w-full"
                                          >
                                            <SelectItem value="pcs">
                                              Pieces
                                            </SelectItem>
                                            <SelectItem value="kg">
                                              Kilograms
                                            </SelectItem>
                                            <SelectItem value="m">
                                              Meters
                                            </SelectItem>
                                            <SelectItem value="sqm">
                                              Square Meters
                                            </SelectItem>
                                            <SelectItem value="bags">
                                              Bags
                                            </SelectItem>
                                            <SelectItem value="tons">
                                              Tons
                                            </SelectItem>
                                            <SelectItem value="litres">
                                              Litres
                                            </SelectItem>
                                            <SelectItem value="lorries">
                                              Lorries
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>

                                    <div className="flex justify-end">
                                      <Button
                                        type="button"
                                        onClick={handleAddMaterial}
                                        className="bg-orange-500 hover:bg-orange-600"
                                      >
                                        {editingMaterialIndex !== null
                                          ? "Update Material"
                                          : "Add Material"}
                                      </Button>
                                    </div>

                                    {/* Phase Materials List */}
                                    {newPhase.materials &&
                                      newPhase.materials.length > 0 && (
                                        <div className="mt-4">
                                          <h4 className="font-medium mb-2">
                                            Phase Materials:
                                          </h4>
                                          <div className="border rounded-md overflow-hidden">
                                            <Table>
                                              <TableHeader>
                                                <TableRow>
                                                  <TableHead>
                                                    Material Name
                                                  </TableHead>
                                                  <TableHead>
                                                    Category
                                                  </TableHead>
                                                  <TableHead>
                                                    Quantity
                                                  </TableHead>
                                                  <TableHead className="text-right">
                                                    Actions
                                                  </TableHead>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {newPhase.materials.map(
                                                  (material, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell className="font-medium">
                                                        {material.name}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.category}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.quantity}{" "}
                                                        {material.unit}
                                                      </TableCell>
                                                      <TableCell className="text-right">
                                                        <div className="flex justify-end gap-2">
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleEditMaterial(
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6"
                                                          >
                                                            <Pencil className="h-3 w-3" />
                                                          </Button>
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleDeleteMaterial(
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6 text-red-500"
                                                          >
                                                            <Trash2 className="h-3 w-3" />
                                                          </Button>
                                                        </div>
                                                      </TableCell>
                                                    </TableRow>
                                                  )
                                                )}
                                              </TableBody>
                                            </Table>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex justify-end mt-4">
                            <Button
                              type="button"
                              onClick={handleAddPhase}
                              className="bg-orange-500 hover:bg-orange-600"
                            >
                              {editingPhaseIndex !== null
                                ? "Update Phase"
                                : "Add Phase"}
                            </Button>
                          </div>

                          {/* Added Phases List */}
                          {formData.phases.length > 0 && (
                            <div className="mt-6">
                              <h4 className="font-medium mb-4">
                                Added Phases:
                              </h4>
                              <div className="space-y-4">
                                {formData.phases.map((phase, index) => (
                                  <div
                                    key={index}
                                    className="border rounded-lg p-4 bg-muted/50"
                                  >
                                    <div className="flex justify-between items-start mb-4">
                                      <div>
                                        <h5 className="font-semibold text-lg">
                                          {phase.name}
                                        </h5>
                                        <span className="text-sm text-muted-foreground">
                                          {phase.materials?.length || 0}{" "}
                                          materials
                                        </span>
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => handleEditPhase(index)}
                                          className="h-8 w-8"
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() =>
                                            handleDeletePhase(index)
                                          }
                                          className="h-8 w-8 text-red-500"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>

                                    {/* Phase Materials */}
                                    {phase.materials &&
                                      phase.materials.length > 0 && (
                                        <div className="mt-4">
                                          <h6 className="text-sm font-medium mb-2">
                                            Phase Materials:
                                          </h6>
                                          <div className="border rounded-md overflow-hidden">
                                            <Table>
                                              <TableHeader>
                                                <TableRow>
                                                  <TableHead>
                                                    Material Name
                                                  </TableHead>
                                                  <TableHead>
                                                    Category
                                                  </TableHead>
                                                  <TableHead>
                                                    Quantity
                                                  </TableHead>
                                                  <TableHead className="text-right">
                                                    Actions
                                                  </TableHead>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {phase.materials.map(
                                                  (material, matIndex) => (
                                                    <TableRow key={matIndex}>
                                                      <TableCell className="font-medium">
                                                        {material.name}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.category}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.quantity}{" "}
                                                        {material.unit}
                                                      </TableCell>
                                                      <TableCell className="text-right">
                                                        <div className="flex justify-end gap-2">
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleEditMaterial(
                                                                matIndex,
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6"
                                                          >
                                                            <Pencil className="h-3 w-3" />
                                                          </Button>
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleDeleteMaterial(
                                                                matIndex,
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6 text-red-500"
                                                          >
                                                            <Trash2 className="h-3 w-3" />
                                                          </Button>
                                                        </div>
                                                      </TableCell>
                                                    </TableRow>
                                                  )
                                                )}
                                              </TableBody>
                                            </Table>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tools Section */}
                  <div className="grid gap-2 mt-6 border-t pt-4">
                    <div className="flex justify-between items-center">
                      <Label>Loose Tools</Label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowToolsSection(!showToolsSection)}
                        className="text-xs"
                      >
                        {showToolsSection ? "Hide" : "Add Tools"}{" "}
                        <Package className="ml-2 h-4 w-4" />
                      </Button>
                    </div>

                    {showToolsSection && (
                      <div className="border rounded-md p-4 mt-2">
                        <div className="grid gap-4">
                          {/* Tool Form */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="grid gap-2">
                              <Label htmlFor="toolCategory">
                                Tool Category
                              </Label>
                              <Select
                                value={newTool.category}
                                onValueChange={(value) => {
                                  setNewTool((prev) => ({
                                    ...prev,
                                    category: value,
                                    name: "", // Reset name when category changes
                                  }));
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select tool category" />
                                </SelectTrigger>
                                <SelectContent
                                  position="popper"
                                  className="w-full"
                                >
                                  {TOOL_CATEGORIES.map((category) => (
                                    <SelectItem key={category} value={category}>
                                      {category}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {newTool.category && (
                              <div className="grid gap-2">
                                <Label htmlFor="toolType">Tool Type</Label>
                                <Select
                                  value={newTool.name}
                                  onValueChange={(value) => {
                                    setNewTool((prev) => ({
                                      ...prev,
                                      name: value,
                                    }));
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select tool type" />
                                  </SelectTrigger>
                                  <SelectContent
                                    position="popper"
                                    className="w-full"
                                  >
                                    {MATERIAL_ITEMS[newTool.category]?.map(
                                      (tool) => (
                                        <SelectItem key={tool} value={tool}>
                                          {tool}
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            <div className="grid gap-2">
                              <Label htmlFor="toolQuantity">Quantity</Label>
                              <Input
                                id="toolQuantity"
                                type="number"
                                min="1"
                                value={newTool.quantity}
                                onChange={(e) =>
                                  setNewTool((prev) => ({
                                    ...prev,
                                    quantity: Number(e.target.value),
                                  }))
                                }
                              />
                            </div>

                            <div className="grid gap-2">
                              <Label htmlFor="toolUnit">Unit</Label>
                              <Select
                                value={newTool.unit}
                                onValueChange={(value) =>
                                  setNewTool((prev) => ({
                                    ...prev,
                                    unit: value,
                                  }))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select unit" />
                                </SelectTrigger>
                                <SelectContent
                                  position="popper"
                                  className="w-full"
                                >
                                  <SelectItem value="pcs">Pieces</SelectItem>
                                  <SelectItem value="sets">Sets</SelectItem>
                                  <SelectItem value="units">Units</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="flex justify-end">
                            <Button
                              type="button"
                              onClick={handleAddTool}
                              className="bg-orange-500 hover:bg-orange-600"
                            >
                              {editingToolIndex !== null
                                ? "Update Tool"
                                : "Add Tool"}
                            </Button>
                          </div>

                          {/* Tools List */}
                          {formData.tools && formData.tools.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium mb-2">Added Tools:</h4>
                              <div className="border rounded-md overflow-hidden">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Tool Name</TableHead>
                                      <TableHead>Category</TableHead>
                                      <TableHead>Quantity</TableHead>
                                      <TableHead className="text-right">
                                        Actions
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {formData.tools.map((tool, index) => (
                                      <TableRow key={index}>
                                        <TableCell className="font-medium">
                                          {tool.name}
                                        </TableCell>
                                        <TableCell>{tool.category}</TableCell>
                                        <TableCell>
                                          {tool.quantity} {tool.unit}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <div className="flex justify-end gap-2">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() =>
                                                handleEditTool(index)
                                              }
                                              className="h-6 w-6"
                                            >
                                              <Pencil className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() =>
                                                handleDeleteTool(index)
                                              }
                                              className="h-6 w-6 text-red-500"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={
                      updateSiteMutation.isPending ||
                      createSiteMutation.isPending
                    }
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      updateSiteMutation.isPending ||
                      createSiteMutation.isPending
                    }
                  >
                    {updateSiteMutation.isPending ||
                    createSiteMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Updating...
                      </div>
                    ) : (
                      "Add"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={updateOpen} onOpenChange={setUpdateOpen}>
            <DialogContent className="max-h-[90vh] overflow-y-auto w-[90vw] max-w-[1200px]">
              <DialogHeader>
                <DialogTitle>Update Site</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter site title"
                      required
                      disabled={updateSiteMutation.isPending}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      placeholder="Enter site location"
                      required
                      disabled={updateSiteMutation.isPending}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="siteType">Site Type</Label>
                    {!customTypeMode ? (
                      <div className="flex gap-2">
                        <Select
                          value={formData.siteType}
                          onValueChange={(value) => {
                            if (value === "add_new") {
                              setCustomTypeMode(true);
                            } else {
                              setFormData((prev) => ({
                                ...prev,
                                siteType: value,
                              }));
                            }
                          }}
                          disabled={updateSiteMutation.isPending}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select site type" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableSiteTypes.map((type) => (
                              <SelectItem key={type} value={type}>
                                {type}
                              </SelectItem>
                            ))}
                            <SelectItem
                              value="add_new"
                              className="text-blue-600"
                            >
                              + Add New Type
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter new site type"
                          value={newSiteType}
                          onChange={(e) => setNewSiteType(e.target.value)}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          onClick={handleAddCustomSiteType}
                          className="bg-orange-500 hover:bg-orange-600"
                        >
                          Add
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setCustomTypeMode(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="siteManager">Site Manager</Label>
                    <Select
                      value={formData.siteManager.id}
                      onValueChange={handleSiteManagerChange}
                      disabled={
                        updateSiteMutation.isPending ||
                        createSiteMutation.isPending
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select site manager" />
                      </SelectTrigger>
                      <SelectContent>
                        {siteManagers?.map((manager: User) => (
                          <SelectItem key={manager._id} value={manager._id}>
                            {manager.name} ({manager.phone})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Phases Section */}
                  <div className="grid gap-2 mt-6 border-t pt-4">
                    <div className="flex justify-between items-center">
                      <Label>Construction Phases</Label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowPhasesSection(!showPhasesSection)}
                        className="text-xs"
                      >
                        {showPhasesSection ? "Hide" : "Add Phases"}{" "}
                        <Calendar className="ml-2 h-4 w-4" />
                      </Button>
                    </div>

                    {showPhasesSection && (
                      <div className="border rounded-md p-4 mt-2">
                        <div className="grid gap-4">
                          {/* Phase Form */}
                          <div className="grid grid-cols-1 gap-3">
                            <div className="grid gap-2">
                              <Label htmlFor="phaseName">Phase Name</Label>
                              <Input
                                id="phaseName"
                                value={newPhase.name}
                                onChange={(e) =>
                                  setNewPhase((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }))
                                }
                                placeholder="Enter phase name (e.g., Phase One)"
                              />
                            </div>

                            {/* Materials Section for Phase */}
                            <div className="grid gap-4 mt-4">
                              <div className="flex justify-between items-center">
                                <Label>Materials Needed for this Phase</Label>
                                <Button
                                  type="button"
                                  variant="outline"
                                  onClick={() =>
                                    setShowMaterialsSection(
                                      !showMaterialsSection
                                    )
                                  }
                                  className="text-xs"
                                >
                                  {showMaterialsSection
                                    ? "Hide"
                                    : "Add Materials"}{" "}
                                  <Package className="ml-2 h-4 w-4" />
                                </Button>
                              </div>

                              {showMaterialsSection && (
                                <div className="border rounded-md p-4">
                                  <div className="grid gap-4">
                                    {/* Material Form */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div className="grid gap-2">
                                        <Label htmlFor="materialCategory">
                                          Material Category
                                        </Label>
                                        <Select
                                          value={newMaterial.category}
                                          onValueChange={(value) => {
                                            setNewMaterial((prev) => ({
                                              ...prev,
                                              category: value,
                                              name: "", // Reset name when category changes
                                            }));
                                          }}
                                        >
                                          <SelectTrigger>
                                            <SelectValue placeholder="Select material category" />
                                          </SelectTrigger>
                                          <SelectContent
                                            position="popper"
                                            className="w-full"
                                          >
                                            {Object.entries(
                                              MATERIAL_CATEGORIES
                                            ).map(([key, value]) => (
                                              <SelectItem
                                                key={key}
                                                value={value}
                                              >
                                                {value}
                                              </SelectItem>
                                            ))}
                                            <SelectItem
                                              value="custom"
                                              className="text-blue-600"
                                            >
                                              + Add Custom Category
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>

                                      {newMaterial.category &&
                                        newMaterial.category !== "custom" && (
                                          <div className="grid gap-2">
                                            <Label htmlFor="materialType">
                                              Material Type
                                            </Label>
                                            <Select
                                              value={newMaterial.name}
                                              onValueChange={(value) => {
                                                if (value === "custom") return;
                                                setNewMaterial((prev) => ({
                                                  ...prev,
                                                  name: value,
                                                }));
                                              }}
                                            >
                                              <SelectTrigger>
                                                <SelectValue placeholder="Select material type" />
                                              </SelectTrigger>
                                              <SelectContent
                                                position="popper"
                                                className="w-full"
                                              >
                                                {MATERIAL_TYPES[
                                                  newMaterial.category
                                                ]?.map((type) => (
                                                  <SelectItem
                                                    key={type}
                                                    value={type}
                                                  >
                                                    {type}
                                                  </SelectItem>
                                                ))}
                                                <SelectItem
                                                  value="custom"
                                                  className="text-blue-600"
                                                >
                                                  + Add Custom Type
                                                </SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}

                                      {newMaterial.category === "custom" && (
                                        <div className="grid gap-2">
                                          <Label htmlFor="customMaterialCategory">
                                            Custom Category Name
                                          </Label>
                                          <Input
                                            id="customMaterialCategory"
                                            value={newMaterial.category}
                                            onChange={(e) =>
                                              setNewMaterial((prev) => ({
                                                ...prev,
                                                category: e.target.value,
                                              }))
                                            }
                                            placeholder="Enter category name"
                                          />
                                        </div>
                                      )}

                                      {newMaterial.name === "custom" && (
                                        <div className="grid gap-2">
                                          <Label htmlFor="customMaterialName">
                                            Custom Material Name
                                          </Label>
                                          <Input
                                            id="customMaterialName"
                                            value=""
                                            onChange={(e) =>
                                              setNewMaterial((prev) => ({
                                                ...prev,
                                                name: e.target.value,
                                              }))
                                            }
                                            placeholder="Enter material name"
                                          />
                                        </div>
                                      )}

                                      <div className="grid gap-2">
                                        <Label htmlFor="materialQuantity">
                                          Quantity
                                        </Label>
                                        <Input
                                          id="materialQuantity"
                                          type="number"
                                          min="1"
                                          value={newMaterial.quantity}
                                          onChange={(e) =>
                                            setNewMaterial((prev) => ({
                                              ...prev,
                                              quantity: Number(e.target.value),
                                            }))
                                          }
                                        />
                                      </div>

                                      <div className="grid gap-2">
                                        <Label htmlFor="materialUnit">
                                          Unit
                                        </Label>
                                        <Select
                                          value={newMaterial.unit}
                                          onValueChange={(value) =>
                                            setNewMaterial((prev) => ({
                                              ...prev,
                                              unit: value,
                                            }))
                                          }
                                        >
                                          <SelectTrigger>
                                            <SelectValue placeholder="Select unit" />
                                          </SelectTrigger>
                                          <SelectContent
                                            position="popper"
                                            className="w-full"
                                          >
                                            <SelectItem value="pcs">
                                              Pieces
                                            </SelectItem>
                                            <SelectItem value="kg">
                                              Kilograms
                                            </SelectItem>
                                            <SelectItem value="m">
                                              Meters
                                            </SelectItem>
                                            <SelectItem value="sqm">
                                              Square Meters
                                            </SelectItem>
                                            <SelectItem value="bags">
                                              Bags
                                            </SelectItem>
                                            <SelectItem value="tons">
                                              Tons
                                            </SelectItem>
                                            <SelectItem value="litres">
                                              Litres
                                            </SelectItem>
                                            <SelectItem value="lorries">
                                              Lorries
                                            </SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    </div>

                                    <div className="flex justify-end">
                                      <Button
                                        type="button"
                                        onClick={handleAddMaterial}
                                        className="bg-orange-500 hover:bg-orange-600"
                                      >
                                        {editingMaterialIndex !== null
                                          ? "Update Material"
                                          : "Add Material"}
                                      </Button>
                                    </div>

                                    {/* Phase Materials List */}
                                    {newPhase.materials &&
                                      newPhase.materials.length > 0 && (
                                        <div className="mt-4">
                                          <h4 className="font-medium mb-2">
                                            Phase Materials:
                                          </h4>
                                          <div className="border rounded-md overflow-hidden">
                                            <Table>
                                              <TableHeader>
                                                <TableRow>
                                                  <TableHead>
                                                    Material Name
                                                  </TableHead>
                                                  <TableHead>
                                                    Category
                                                  </TableHead>
                                                  <TableHead>
                                                    Quantity
                                                  </TableHead>
                                                  <TableHead className="text-right">
                                                    Actions
                                                  </TableHead>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {newPhase.materials.map(
                                                  (material, index) => (
                                                    <TableRow key={index}>
                                                      <TableCell className="font-medium">
                                                        {material.name}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.category}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.quantity}{" "}
                                                        {material.unit}
                                                      </TableCell>
                                                      <TableCell className="text-right">
                                                        <div className="flex justify-end gap-2">
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleEditMaterial(
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6"
                                                          >
                                                            <Pencil className="h-3 w-3" />
                                                          </Button>
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleDeleteMaterial(
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6 text-red-500"
                                                          >
                                                            <Trash2 className="h-3 w-3" />
                                                          </Button>
                                                        </div>
                                                      </TableCell>
                                                    </TableRow>
                                                  )
                                                )}
                                              </TableBody>
                                            </Table>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex justify-end mt-4">
                            <Button
                              type="button"
                              onClick={handleAddPhase}
                              className="bg-orange-500 hover:bg-orange-600"
                            >
                              {editingPhaseIndex !== null
                                ? "Update Phase"
                                : "Add Phase"}
                            </Button>
                          </div>

                          {/* Added Phases List */}
                          {formData.phases.length > 0 && (
                            <div className="mt-6">
                              <h4 className="font-medium mb-4">
                                Added Phases:
                              </h4>
                              <div className="space-y-4">
                                {formData.phases.map((phase, index) => (
                                  <div
                                    key={index}
                                    className="border rounded-lg p-4 bg-muted/50"
                                  >
                                    <div className="flex justify-between items-start mb-4">
                                      <div>
                                        <h5 className="font-semibold text-lg">
                                          {phase.name}
                                        </h5>
                                        <span className="text-sm text-muted-foreground">
                                          {phase.materials?.length || 0}{" "}
                                          materials
                                        </span>
                                      </div>
                                      <div className="flex gap-2">
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => handleEditPhase(index)}
                                          className="h-8 w-8"
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          onClick={() =>
                                            handleDeletePhase(index)
                                          }
                                          className="h-8 w-8 text-red-500"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>

                                    {/* Phase Materials */}
                                    {phase.materials &&
                                      phase.materials.length > 0 && (
                                        <div className="mt-4">
                                          <h6 className="text-sm font-medium mb-2">
                                            Phase Materials:
                                          </h6>
                                          <div className="border rounded-md overflow-hidden">
                                            <Table>
                                              <TableHeader>
                                                <TableRow>
                                                  <TableHead>
                                                    Material Name
                                                  </TableHead>
                                                  <TableHead>
                                                    Category
                                                  </TableHead>
                                                  <TableHead>
                                                    Quantity
                                                  </TableHead>
                                                  <TableHead className="text-right">
                                                    Actions
                                                  </TableHead>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {phase.materials.map(
                                                  (material, matIndex) => (
                                                    <TableRow key={matIndex}>
                                                      <TableCell className="font-medium">
                                                        {material.name}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.category}
                                                      </TableCell>
                                                      <TableCell>
                                                        {material.quantity}{" "}
                                                        {material.unit}
                                                      </TableCell>
                                                      <TableCell className="text-right">
                                                        <div className="flex justify-end gap-2">
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleEditMaterial(
                                                                matIndex,
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6"
                                                          >
                                                            <Pencil className="h-3 w-3" />
                                                          </Button>
                                                          <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() =>
                                                              handleDeleteMaterial(
                                                                matIndex,
                                                                index
                                                              )
                                                            }
                                                            className="h-6 w-6 text-red-500"
                                                          >
                                                            <Trash2 className="h-3 w-3" />
                                                          </Button>
                                                        </div>
                                                      </TableCell>
                                                    </TableRow>
                                                  )
                                                )}
                                              </TableBody>
                                            </Table>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tools Section */}
                  <div className="grid gap-2 mt-6 border-t pt-4">
                    <div className="flex justify-between items-center">
                      <Label>Loose Tools</Label>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowToolsSection(!showToolsSection)}
                        className="text-xs"
                      >
                        {showToolsSection ? "Hide" : "Add Tools"}{" "}
                        <Package className="ml-2 h-4 w-4" />
                      </Button>
                    </div>

                    {showToolsSection && (
                      <div className="border rounded-md p-4 mt-2">
                        <div className="grid gap-4">
                          {/* Tool Form */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="grid gap-2">
                              <Label htmlFor="toolCategory">
                                Tool Category
                              </Label>
                              <Select
                                value={newTool.category}
                                onValueChange={(value) => {
                                  setNewTool((prev) => ({
                                    ...prev,
                                    category: value,
                                    name: "", // Reset name when category changes
                                  }));
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select tool category" />
                                </SelectTrigger>
                                <SelectContent
                                  position="popper"
                                  className="w-full"
                                >
                                  {TOOL_CATEGORIES.map((category) => (
                                    <SelectItem key={category} value={category}>
                                      {category}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>

                            {newTool.category && (
                              <div className="grid gap-2">
                                <Label htmlFor="toolType">Tool Type</Label>
                                <Select
                                  value={newTool.name}
                                  onValueChange={(value) => {
                                    setNewTool((prev) => ({
                                      ...prev,
                                      name: value,
                                    }));
                                  }}
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select tool type" />
                                  </SelectTrigger>
                                  <SelectContent
                                    position="popper"
                                    className="w-full"
                                  >
                                    {MATERIAL_ITEMS[newTool.category]?.map(
                                      (tool) => (
                                        <SelectItem key={tool} value={tool}>
                                          {tool}
                                        </SelectItem>
                                      )
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            <div className="grid gap-2">
                              <Label htmlFor="toolQuantity">Quantity</Label>
                              <Input
                                id="toolQuantity"
                                type="number"
                                min="1"
                                value={newTool.quantity}
                                onChange={(e) =>
                                  setNewTool((prev) => ({
                                    ...prev,
                                    quantity: Number(e.target.value),
                                  }))
                                }
                              />
                            </div>

                            <div className="grid gap-2">
                              <Label htmlFor="toolUnit">Unit</Label>
                              <Select
                                value={newTool.unit}
                                onValueChange={(value) =>
                                  setNewTool((prev) => ({
                                    ...prev,
                                    unit: value,
                                  }))
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select unit" />
                                </SelectTrigger>
                                <SelectContent
                                  position="popper"
                                  className="w-full"
                                >
                                  <SelectItem value="pcs">Pieces</SelectItem>
                                  <SelectItem value="sets">Sets</SelectItem>
                                  <SelectItem value="units">Units</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <div className="flex justify-end">
                            <Button
                              type="button"
                              onClick={handleAddTool}
                              className="bg-orange-500 hover:bg-orange-600"
                            >
                              {editingToolIndex !== null
                                ? "Update Tool"
                                : "Add Tool"}
                            </Button>
                          </div>

                          {/* Tools List */}
                          {formData.tools && formData.tools.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium mb-2">Added Tools:</h4>
                              <div className="border rounded-md overflow-hidden">
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Tool Name</TableHead>
                                      <TableHead>Category</TableHead>
                                      <TableHead>Quantity</TableHead>
                                      <TableHead className="text-right">
                                        Actions
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {formData.tools.map((tool, index) => (
                                      <TableRow key={index}>
                                        <TableCell className="font-medium">
                                          {tool.name}
                                        </TableCell>
                                        <TableCell>{tool.category}</TableCell>
                                        <TableCell>
                                          {tool.quantity} {tool.unit}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          <div className="flex justify-end gap-2">
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() =>
                                                handleEditTool(index)
                                              }
                                              className="h-6 w-6"
                                            >
                                              <Pencil className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              onClick={() =>
                                                handleDeleteTool(index)
                                              }
                                              className="h-6 w-6 text-red-500"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={updateSiteMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateSiteMutation.isPending}>
                    {updateSiteMutation.isPending ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Updating...
                      </div>
                    ) : (
                      "Update"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default Sites;
